import { useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import { useLang, t } from '../lib/lang'
import { useMediaQuery } from '../lib/useMediaQuery'

export interface NavVisible {
  progetto: boolean
  singoli: boolean
  videos: boolean
  live: boolean
  contatti: boolean
}

const linkStyle: CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500,
  letterSpacing: '0.18em', textTransform: 'uppercase',
  color: 'var(--fg-inverse)', textDecoration: 'none',
  opacity: 0.82, transition: 'opacity 140ms',
}

function hover(e: React.MouseEvent<HTMLAnchorElement>) { e.currentTarget.style.opacity = '1' }
function unhover(e: React.MouseEvent<HTMLAnchorElement>) { e.currentTarget.style.opacity = '0.82' }

interface LinkDef { href: string; label: string }

function buildLinks(visible: NavVisible, lang: 'it' | 'en'): LinkDef[] {
  const out: LinkDef[] = []
  if (visible.progetto) out.push({ href: '#progetto', label: t(lang, 'nav_project') })
  if (visible.singoli)  out.push({ href: '#singoli',  label: t(lang, 'nav_singoli') })
  if (visible.videos)   out.push({ href: '#videos',   label: t(lang, 'nav_videos') })
  if (visible.live)     out.push({ href: '#live',     label: t(lang, 'nav_live') })
  if (visible.contatti) out.push({ href: '#contatti', label: t(lang, 'nav_contatti') })
  return out
}

function LangSwitch({ light = false }: { light?: boolean }) {
  const { lang, setLang } = useLang()
  const color = light ? 'var(--fg-inverse)' : 'var(--fg-inverse)'
  const base: CSSProperties = {
    background: 'none', border: 0, padding: 0, cursor: 'pointer',
    color, fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 500,
    letterSpacing: '0.18em', textTransform: 'uppercase',
  }
  return (
    <div style={{
      display: 'flex', gap: 10, alignItems: 'center',
      paddingLeft: 16, borderLeft: '1px solid var(--line-inverse)',
    }}>
      <button type="button" onClick={() => setLang('it')} aria-pressed={lang === 'it'}
              style={{ ...base, opacity: lang === 'it' ? 1 : 0.45 }}>IT</button>
      <span style={{ opacity: 0.35, color }}>·</span>
      <button type="button" onClick={() => setLang('en')} aria-pressed={lang === 'en'}
              style={{ ...base, opacity: lang === 'en' ? 1 : 0.45 }}>EN</button>
    </div>
  )
}

function Burger({ open, onClick }: { open: boolean; onClick: () => void }) {
  const bar: CSSProperties = {
    display: 'block', width: 22, height: 2,
    background: 'var(--fg-inverse)',
    transition: 'transform 200ms var(--ease-smooth), opacity 200ms var(--ease-smooth)',
  }
  return (
    <button type="button" aria-label={open ? 'Chiudi menu' : 'Apri menu'}
            onClick={onClick}
            style={{
              background: 'none', border: 0, padding: 8, cursor: 'pointer',
              display: 'inline-flex', flexDirection: 'column', gap: 5,
            }}>
      <span style={{ ...bar, transform: open ? 'translateY(7px) rotate(45deg)' : 'none' }} />
      <span style={{ ...bar, opacity: open ? 0 : 1 }} />
      <span style={{ ...bar, transform: open ? 'translateY(-7px) rotate(-45deg)' : 'none' }} />
    </button>
  )
}

function MobileDrawer({
  open, links, onClose,
}: { open: boolean; links: LinkDef[]; onClose: () => void }) {
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [open])

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 60,
      background: 'var(--ink)', color: 'var(--fg-inverse)',
      display: 'flex', flexDirection: 'column',
      padding: '90px 28px 40px',
      transform: open ? 'translateY(0)' : 'translateY(-100%)',
      transition: 'transform 300ms var(--ease-smooth)',
      pointerEvents: open ? 'auto' : 'none',
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: 20 }}>
        {links.map((l) => (
          <a key={l.href} href={l.href} onClick={onClose} style={{
            fontFamily: 'var(--font-display)', fontWeight: 500,
            textTransform: 'uppercase', letterSpacing: '0.04em',
            fontSize: 32, lineHeight: 1.1,
            color: 'var(--fg-inverse)', textDecoration: 'none',
          }}>{l.label}</a>
        ))}
      </div>
      <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 16 }}>
        <LangSwitch />
      </div>
    </div>
  )
}

export function Nav({ visible }: { visible: NavVisible }) {
  const { lang } = useLang()
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState(false)
  const isMobile = useMediaQuery('(max-width: 820px)')
  const links = buildLinks(visible, lang)

  useEffect(() => {
    function onScroll() { setScrolled(window.scrollY > 60) }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (!isMobile) setOpen(false)
  }, [isMobile])

  return (
    <>
      <nav className="ax-nav" style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        padding: '22px 48px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        color: 'var(--fg-inverse)',
        background: scrolled || open ? 'var(--ink)' : 'transparent',
        borderBottom: (scrolled && !open) ? '1px solid var(--line-inverse)' : '1px solid transparent',
        transition: 'background 240ms var(--ease-smooth), border-color 240ms var(--ease-smooth)',
      }}>
        <a href="#" style={{ display: 'flex', alignItems: 'center' }}>
          <img src="/assets/logo-white-trans.png" alt="Ancreus"
               style={{ height: 64, width: 'auto', display: 'block' }} />
        </a>

        {isMobile ? (
          <Burger open={open} onClick={() => setOpen((v) => !v)} />
        ) : (
          <div style={{ display: 'flex', gap: 36, alignItems: 'center' }}>
            {links.map((l) => (
              <a key={l.href} href={l.href} style={linkStyle} onMouseEnter={hover} onMouseLeave={unhover}>
                {l.label}
              </a>
            ))}
            <LangSwitch />
          </div>
        )}
      </nav>

      {isMobile && <MobileDrawer open={open} links={links} onClose={() => setOpen(false)} />}
    </>
  )
}
